/* Glue to the welcome feature's gate (features/welcome/gate.ts): the onboarding / 18+ gate for RequireAuth
   and aliases for URLs the API sends people to. Loaded through import.meta.glob so the shell still works
   (with plain fallbacks) when the welcome feature is absent. */
export interface GateResult { checking: boolean; redirect: string | null }
interface GateModule {
  useOnboardingGate: () => GateResult;
  welcomeAlias: (pathname: string, search?: string) => string | null;
  WELCOME_ROUTE?: string;
}

const mods = import.meta.glob<GateModule>('../features/welcome/gate.ts', { eager: true });
const gate: GateModule | null = Object.values(mods)[0] ?? null;

const noGate = (): GateResult => ({ checking: false, redirect: null });
/** `{checking, redirect}` for the signed-in user (never blocks on an unreachable onboarding route). */
export const useOnboardingGate: () => GateResult = gate?.useOnboardingGate ?? noGate;

export const WELCOME = gate?.WELCOME_ROUTE ?? '/welcome';

/** URLs the API and old links use: Google callback for new accounts, Google errors, reset email, sign-up links. */
export const ALIAS_PATHS = ['/onboarding', '/onboarding/age', '/sign-in', '/signin', '/signup', '/sign-up', '/reset-password', '/login'] as const;

const FALLBACK: Record<string, string> = {
  '/onboarding': WELCOME, '/onboarding/age': WELCOME, '/sign-in': WELCOME, '/signin': WELCOME, '/login': WELCOME,
  '/signup': WELCOME, '/sign-up': WELCOME, '/reset-password': WELCOME,
};

/** Where an alias URL goes (keeps the query: `?next=`, `?error=`, `?token=`). */
export function aliasTarget(pathname: string, search = ''): string {
  const p = pathname.replace(/\/+$/, '') || '/';
  const viaFeature = gate?.welcomeAlias(p, search);
  if (viaFeature) return viaFeature;
  if (p === '/login') return gate?.welcomeAlias('/sign-in', search) ?? `${WELCOME}${search}`;
  return `${FALLBACK[p] ?? WELCOME}${search}`;
}

export const hasWelcomeGate = gate !== null;
